import React, { useState, useEffect, useMemo } from "react";

type ColorsAndImages = {
  color: string;
  images: (string | undefined)[];
};

interface ProductCardImageCarouselProps {
  productId: string;
  selectedColor: string | undefined;
  colorAndImages: ColorsAndImages[];
  colorSelector: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLImageElement>;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
}

const ProductCardImageCarousel: React.FC<ProductCardImageCarouselProps> = ({
  productId,
  selectedColor,
  colorAndImages,
  colorSelector,
  onClick,
  onLoad,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hovering, setHovering] = useState(false);

  const images = useMemo(
    () =>
      !!selectedColor
        ? colorAndImages?.find((c) => c.color === selectedColor)?.images
        : (colorAndImages?.map((c) => c.images?.[0]) ?? []),
    [selectedColor, colorAndImages],
  );

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (hovering && (images?.length ?? 0) > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % (images?.length ?? 1));
      }, 1500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hovering, images]);

  return (
    <div className="w-full h-full bg-white shadow hover:shadow-lg transition">
      <div className="w-full h-full overflow-hidden cursor-pointer">
        <img
          src={images?.[currentImageIndex]}
          alt={`${productId + "_" + selectedColor} product`}
          className="w-full h-full object-fill transition duration-500"
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => {
            setHovering(false);
            setCurrentImageIndex(0); // 👈 reset to first image
          }}
          onClick={onClick}
          onLoad={(e) => {
            if (!!images?.[currentImageIndex]) {
              onLoad?.(e);
            }
          }}
        />
        {!!colorSelector && (
          <div className="w-full absolute bottom-1 left-1/2 -translate-x-1/2 flex flex-row justify-center space-x-2 px-4 py-2">
            {colorSelector}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCardImageCarousel;
